import React, { useEffect, useRef } from "react";
import { useScreenCloud } from "../ScreenCloudReactApp";
import SlideShowProvider from "./SlideShowProvider";
import Slide from "./Slide";
import { usePulse } from "../providers/PulseProvider";

const SlideShow = (): JSX.Element => {
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);
  const [currentSlideIndex, setCurrentSlideIndex] = React.useState(0);
  const sc = useScreenCloud();
  const { isPulse } = usePulse();
  const slideCount = sc.config.slideCount;
  const baseUrl = sc.config.url;
  let embedUrl = "";
  if (!baseUrl.endsWith("?embed")) {
    embedUrl = baseUrl + "?embed";
  } else {
    embedUrl = baseUrl;
  }

  const getNextSlideIndex = () => {
    return currentSlideIndex === slideCount - 1 ? 0 : currentSlideIndex + 1;
  };

  const slides = useRef(
    new Array(
      slideCount < currentSlideIndex + 1 ? currentSlideIndex + 1 : slideCount
    )
      .fill(null)
      .map((slide, index) => `${embedUrl}#${index + 1}`)
  ).current;

  const finishingEmittedRef = useRef(false);
  const canFinishRef = useRef(false);
  const finishTimerRef = useRef<number | null>(null);
  const onlyItemOnScreen = Boolean(
    (sc.context as unknown as { onlyItemOnScreen?: boolean }).onlyItemOnScreen
  );

  // If there are no slides configured, finish immediately with an error only in pulse
  useEffect(() => {
    if (!isPulse) {
      return;
    }
    if (!slideCount || slideCount <= 0) {
      sc.emitFinished({
        error: {
          code: 404,
          message: "No slides to display",
        },
      });
    }
  }, [sc, slideCount, isPulse]);

  // On first load in pulse, estimate finishing time as min(slides*12s, minDurationMS)
  useEffect(() => {
    if (!isPulse || finishingEmittedRef.current) {
      return;
    }
    if (slideCount && slideCount > 0) {
      const minDuration = (sc.context as unknown as { minDurationMS?: number })
        ?.minDurationMS;
      const maxDuration = (sc.context as unknown as { maxDurationMS?: number })
        ?.maxDurationMS;
      const slideTotalMs = slideCount * 12000;
      const estimatedMs = Math.min(
        typeof minDuration === "number"
          ? minDuration
          : Number.POSITIVE_INFINITY,
        slideTotalMs,
        typeof maxDuration === "number" ? maxDuration : Number.POSITIVE_INFINITY
      );
      sc.emitFinishing({ durationMS: estimatedMs });
      finishingEmittedRef.current = true;
    }
  }, [isPulse, slideCount, sc]);

  // Start min duration timer; after it elapses, finish after the current slide
  useEffect(() => {
    if (!isPulse) {
      return;
    }
    if (finishTimerRef.current) {
      window.clearTimeout(finishTimerRef.current);
      finishTimerRef.current = null;
    }
    // Use min(slides*12s, minDurationMS) as the min duration
    const minDuration = (sc.context as unknown as { minDurationMS?: number })
      ?.minDurationMS;
    const maxDuration = (sc.context as unknown as { maxDurationMS?: number })
      ?.maxDurationMS;
    const slideTotalMs = slideCount * 12000;
    const estimatedMs = Math.min(
      typeof minDuration === "number" ? minDuration : Number.POSITIVE_INFINITY,
      slideTotalMs,
      typeof maxDuration === "number" ? maxDuration : Number.POSITIVE_INFINITY
    );
    finishTimerRef.current = window.setTimeout(() => {
      canFinishRef.current = true;
    }, estimatedMs);

    // Hard cutoff for maxDuration
    let maxTimer: number | null = null;
    if (typeof maxDuration === "number") {
      maxTimer = window.setTimeout(() => {
        sc.emitFinished();
      }, maxDuration);
    }

    return () => {
      if (finishTimerRef.current) {
        window.clearTimeout(finishTimerRef.current);
      }
      if (maxTimer) {
        window.clearTimeout(maxTimer);
      }
    };
  }, [isPulse, slideCount, sc]);

  const slots = [
    // active slot
    currentSlideIndex,
    // preload slot
    ...(slideCount > 1 ? [getNextSlideIndex()] : []),
  ];

  return (
    <SlideShowProvider
      onSlideFinished={() => {
        if (isPulse) {
          // If min duration elapsed, finish after current slide completes
          if (canFinishRef.current) {
            canFinishRef.current = false;
            sc.emitFinished();
            return;
          }
          // If not only item on screen, end on the last slide even if min duration not reached
          if (!onlyItemOnScreen && getNextSlideIndex() === 0) {
            sc.emitFinished();
            return;
          }
        }
        if (slideCount > 1) {
          setActiveSlideIndex(getNextSlideIndex());
        }
      }}
      onSlideExited={() => {
        if (slideCount > 1) {
          setCurrentSlideIndex(activeSlideIndex);
        }
      }}
    >
      {slots.map((slideIndex, index) => (
        <Slide
          url={slides[slideIndex]}
          key={slides[slideIndex]}
          in={slideIndex === activeSlideIndex}
          isActive={activeSlideIndex === slideIndex}
        />
      ))}
    </SlideShowProvider>
  );
};
export default SlideShow;
